import { useEffect, useId, useRef, useState } from 'react'
import { Figure } from './figures/Figure'
import { Icon } from './Icon'
import { emphasis, type Tutorial } from '../lib/tutorials'

/**
 * One walkthrough step, on a card that stays beside the screen while somebody works it.
 *
 * It replaced a modal. The modal made the screen it described inert - and on a phone covered it - and
 * following a step's link had to close it, so a reader arrived on the screen the step was about with
 * nothing telling them what to do there. The card is not modal and survives the navigation, so the
 * step is still beside the screen it points at.
 *
 * **Fixed-position, not a grid track.** D-443 refused a fourth `.shell-body` column for the inspector
 * because it re-measures the content column on every route, which is the sideways-scroll class the
 * layout gate exists to catch. A fixed card takes no width from the grid at all: bottom-right above
 * the status bar from 700px up, and above the tab bar below that. It collapses to a single line when
 * it is in the way.
 *
 * The step lives in the URL and in storage and this component is told which one to draw, so there is
 * no step here to fall out of step with the address bar.
 */
export function CoachCard({
  tutorial,
  step: current,
  focusRequest,
  onStep,
  onFollow,
  onPutDown,
  onDone
}: {
  tutorial: Tutorial
  /** One-based, already clamped by the caller. */
  step: number
  /** Changes when the reader asked for the card, which is the only time it may take focus. */
  focusRequest: number
  onStep: (step: number) => void
  onFollow: (to: string) => void
  /** The cross. Keeps the reader's place, and takes the card off the screen (D-498). */
  onPutDown: () => void
  /** Finished, at the last step. The only thing that forgets where the reader got to. */
  onDone: () => void
}) {
  const [collapsed, setCollapsed] = useState(false)
  const heading = useRef<HTMLHeadingElement>(null)
  const titleId = useId()

  const step = tutorial.steps[current - 1]!
  const first = current === 1
  const last = current === tutorial.steps.length

  /*
    Focus moves to the step heading when the reader asks for the card or changes step, so a screen
    reader is told what changed - and never when the card merely appears on page load, because an
    operator arriving on a shared link may already be typing.
  */
  useEffect(() => {
    if (focusRequest === 0) return
    setCollapsed(false)
    // After the expand has rendered, or there is no heading to focus.
    requestAnimationFrame(() => heading.current?.focus())
  }, [focusRequest])

  return (
    <section
      className={collapsed ? 'coach-card collapsed' : 'coach-card'}
      aria-labelledby={titleId}
      data-testid="coach-card"
      onKeyDown={(event) => {
        // Escape folds the card rather than closing it, and stops here: the shell maps Escape to the
        // screen's own dismissal, and one keystroke must not both fold this and close the inspector.
        if (event.key === 'Escape' && !collapsed) {
          event.stopPropagation()
          setCollapsed(true)
        }
      }}
    >
      <header className="coach-head">
        <Icon name={tutorial.icon} size={16} />
        <span id={titleId} className="coach-title">
          {tutorial.title}
        </span>
        {/* The place, kept visible when the card is folded to one line. Written out in full in the
            body when it is open, where there is room for the words. */}
        {collapsed && (
          <span className="coach-count" aria-hidden="true">
            {current}/{tutorial.steps.length}
          </span>
        )}
        <button
          type="button"
          className="coach-icon-button"
          aria-expanded={!collapsed}
          aria-label={collapsed ? 'Show this step' : 'Fold this step away'}
          onClick={() => setCollapsed((was) => !was)}
          data-testid="coach-collapse"
        >
          <Icon name="caret-down" size={14} className={collapsed ? 'coach-caret up' : 'coach-caret'} />
        </button>
        <button
          type="button"
          className="coach-icon-button"
          aria-label="Put this walkthrough down"
          onClick={onPutDown}
          data-testid="coach-close"
        >
          <Icon name="x" size={14} />
        </button>
      </header>

      {!collapsed && (
        <>
          <div className="coach-body" data-testid="tutorial-step">
            <p className="coach-progress" data-testid="tutorial-progress">
              Step {current} of {tutorial.steps.length}
            </p>

            {step.figure && <Figure spec={step.figure} />}

            <h4 ref={heading} tabIndex={-1} data-testid="tutorial-step-title">
              {step.title}
            </h4>

            {step.body.map((line, index) => (
              <p key={index}>
                {emphasis(line).map((part, at) =>
                  part.strong ? <strong key={at}>{part.text}</strong> : <span key={at}>{part.text}</span>
                )}
              </p>
            ))}

            {step.link && (
              <button
                type="button"
                className="coach-go"
                onClick={() => onFollow(step.link!.to)}
                data-testid="tutorial-link"
              >
                {step.link.label}
                <Icon name="arrow-right" size={14} />
              </button>
            )}

            {last && (
              <p className="coach-outcome" data-testid="tutorial-outcome">
                <Icon name="check-circle" size={16} />
                <span>{tutorial.outcome}</span>
              </p>
            )}
          </div>

          <footer className="coach-foot">
            {/* One dot per step, each a way straight to it, so the length of the walkthrough and the
                reader's place in it are one glance rather than a sentence. */}
            <ol className="coach-dots" aria-label="Steps">
              {tutorial.steps.map((each, index) => (
                <li key={each.title}>
                  <button
                    type="button"
                    className={index + 1 === current ? 'coach-dot current' : index + 1 < current ? 'coach-dot done' : 'coach-dot'}
                    aria-label={`Step ${index + 1}: ${each.title}`}
                    aria-current={index + 1 === current ? 'step' : undefined}
                    onClick={() => onStep(index + 1)}
                  />
                </li>
              ))}
            </ol>

            <div className="coach-actions">
              {/* Absent on the first step rather than disabled. A dead control on step one is a thing
                  to wonder about, and wondering is what this feature exists to prevent. */}
              {!first && (
                <button type="button" onClick={() => onStep(current - 1)} data-testid="tutorial-back">
                  Back
                </button>
              )}
              {last ? (
                <button type="button" className="primary" onClick={onDone} data-testid="tutorial-done">
                  Done
                </button>
              ) : (
                <button type="button" className="primary" onClick={() => onStep(current + 1)} data-testid="tutorial-next">
                  Next
                </button>
              )}
            </div>
          </footer>
        </>
      )}
    </section>
  )
}
